/**
 * TanStack Query client factory.
 *
 * The factory pattern avoids sharing a single client across requests on the
 * server (each request gets a fresh client), while the browser instance is
 * singleton via the `QueryClientProvider` in `providers.tsx`.
 */

import { QueryClient } from "@tanstack/react-query";

import { ApiError } from "@/lib/api-client";

/**
 * staleTime for analytics payloads (maps, matrices, diagnosis
 * aggregates). The underlying parquet tables change once per model/ETL
 * run — not per minute — so re-fetching multi-MB payloads on the 60 s
 * default only re-parses identical data on the main thread. Perf plan
 * E2.3: heavy hooks opt in via this constant so the policy lives in
 * one place.
 */
export const ANALYTICS_STALE_TIME = 30 * 60 * 1000;

/**
 * gcTime sibling for the same payloads. Must be >= `ANALYTICS_STALE_TIME`.
 *
 * `staleTime > gcTime` is unreachable configuration: an entry declared
 * fresh for 30 minutes was being destroyed at TanStack's 5-minute gcTime
 * default, so the long staleTime the 15 analytics hooks opt into could
 * never actually be reached across a navigation. `use-scope-table.ts`
 * already worked around this locally — its comment states the failure
 * mode outright: "Without it the cache is garbage-collected between
 * navigations and the payload is re-pulled."
 *
 * Kept next to the staleTime it guards so the pair can be read, and
 * tested, as one policy. The invariant is asserted in
 * `tests/lib/query-client.test.ts`.
 */
export const ANALYTICS_GC_TIME = ANALYTICS_STALE_TIME;

/** Predicate driving the global retry policy.
 *
 * The api container takes ~30 s to warm up after a code reload (predict
 * caches, ADLS pre-fetch, etc. — see ``app/main.py`` lifespan). During
 * that window every BFF call returns 502 ``upstream_unreachable`` and
 * the picker / landing screens would render their generic
 * "Could not load…" error. Retrying transient 5xx / network failures a
 * few times with exponential backoff papers over the warmup gap; 4xx
 * never retries because those are deterministic client errors. */
export function shouldRetry(failureCount: number, error: unknown): boolean {
  // 2026-07-07 incident (latency-gated freeze race, see
  // docs/perf-baselines/latency-race-investigation-plan-2026-07-07.md,
  // hypothesis H1): a query cancelled via AbortController (every
  // superseded map fetch during rapid pan/zoom) rejects with a
  // DOMException/Error named "AbortError" — NOT an ApiError, since
  // apiFetch's fetch() call throws before ever reaching the
  // response.ok check. That fell through to the "network layer
  // bombed, retry" branch below, so every single superseded fetch
  // during a rapid-pan burst got retried up to 3 times — under real
  // network latency (not Playwright's near-zero mock), those retries
  // compound faster than they drain, producing a growing backlog of
  // in-flight requests for long-stale viewports. Recognize the
  // cancellation and never retry it — it was intentional, not a
  // failure.
  if (error instanceof Error && error.name === "AbortError") return false;
  // Cap at 4 attempts total (initial + 3 retries) — ~7 s of backoff with
  // the default exponential delay covers the 99th-percentile warmup.
  if (failureCount >= 3) return false;
  if (error instanceof ApiError) {
    // 5xx are transient — retry. 4xx are deterministic client errors —
    // don't retry (would just mask bugs). ``status === 0`` is defensive
    // for a future abort/cancellation path; ``apiFetch`` never sets it
    // today.
    return error.status === 0 || error.status >= 500;
  }
  // Plain ``Error`` (e.g. ``TypeError: fetch failed``) — network layer
  // bombed; retry.
  return true;
}

export function makeQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        // Avoid refetch storms in Phase 1; mocks are stable.
        staleTime: 60_000,
        // gcTime was absent, so TanStack's 5-minute default applied to
        // every query — including the analytics hooks that declare
        // themselves fresh for 30 minutes. An entry cannot outlive its
        // own gcTime, so those hooks were silently re-fetching on every
        // navigation. Setting the floor here keeps the policy in one
        // place instead of per-hook workarounds.
        gcTime: ANALYTICS_GC_TIME,
        refetchOnWindowFocus: false,
        // The comment above says "avoid refetch storms"; reconnect was
        // simply missed. Left at its `true` default, a laptop waking from
        // sleep re-fires every mounted stale query at once against an api
        // container running two workers.
        refetchOnReconnect: false,
        retry: shouldRetry,
      },
    },
  });
}
